import { ALL_STORE_SPECIALS_OPTS } from '@iheartjane/data-access';
import {
  storeSpecialsQueryKey,
  useStoreSpecials,
} from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { useQueryClient } from '@tanstack/react-query';
import type { InfiniteData } from '@tanstack/react-query';
import { createContext, useContext, useMemo } from 'react';
import type { PropsWithChildren } from 'react';

import {
  storeSpecialsQueryKeys,
  useGetStoreSpecials,
  useGetStoreSpecialsPage,
} from '@jane/shared-ecomm/data-access';
import type { StoreSpecial } from '@jane/shared/types';

import { useMenu } from '../menuProvider/menuProvider';

export interface SpecialsContextProps {
  fetchNextSpecialsPage: () => void;
  hasNextSpecialsPage: boolean;
  isLoadingStore?: boolean;
  loadingSpecials?: boolean;
  loadingSpecialsCards?: boolean;
  refetchSpecialsPage: () => void;
  specialIds?: StoreSpecial['id'][];
  specials?: StoreSpecial[];
  specialsCards?: StoreSpecial[];
}

export const SpecialsContext = createContext<SpecialsContextProps>(
  {} as SpecialsContextProps
);

type SpecialsProviderProps = PropsWithChildren<{ fetchSpecialsRow?: boolean }>;

export const SPECIALS_PAGE_LIMIT = 16;

export const SpecialsProvider = ({
  children,
  fetchSpecialsRow = true,
}: SpecialsProviderProps) => {
  const queryClient = useQueryClient();
  const { loading, store } = useMenu();
  const storeId = store?.id;
  const useV3 = useFlag(FLAGS.c3UseV3StoreSpecials);

  const v1AllQuery = useGetStoreSpecials(store?.id, {
    enabled: !useV3 && !!store?.id,
  });
  const v3AllQuery = useStoreSpecials({
    ...ALL_STORE_SPECIALS_OPTS,
    enabled: !!useV3 && !!store?.id,
    storeId: store?.id || 0,
  });

  const allSpecials = useV3
    ? ((v3AllQuery.data?.pages.flatMap((page) => page.specials) ??
        []) as unknown as StoreSpecial[])
    : (v1AllQuery.data ?? []);
  const loadingSpecials = useV3 ? v3AllQuery.isLoading : v1AllQuery.isLoading;

  const specialIds = useMemo(
    () => allSpecials.map((special) => special.id),
    [allSpecials]
  );

  const specialsRowEnabled = Boolean(
    store?.specials_row_configs?.enabled && fetchSpecialsRow
  );

  const v1PageQuery = useGetStoreSpecialsPage(
    storeId,
    { enabled: !useV3 && specialsRowEnabled },
    SPECIALS_PAGE_LIMIT
  );
  const v3PageQuery = useStoreSpecials({
    appMode: 'default',
    enabled: !!useV3 && specialsRowEnabled,
    includeHidden: true,
    perPage: SPECIALS_PAGE_LIMIT,
    storeId: storeId || 0,
  });

  const loadingSpecialsCards = useV3
    ? v3PageQuery.isFetching
    : v1PageQuery.isFetching;
  const fetchNextSpecialsPage = useV3
    ? v3PageQuery.fetchNextPage
    : v1PageQuery.fetchNextPage;
  const hasNextSpecialsPage = useV3
    ? v3PageQuery.hasNextPage
    : v1PageQuery.hasNextPage;

  const specialsCards = useMemo(() => {
    if (useV3) {
      return (v3PageQuery.data?.pages?.flatMap((page) => page.specials) ??
        []) as unknown as StoreSpecial[];
    }
    return v1PageQuery.data?.pages?.flatMap((page) => page.specials) ?? [];
  }, [useV3, v3PageQuery.data, v1PageQuery.data]);

  // This doesn't call a "refetch" but it resets the results to the first page
  const handleRefetchSpecialsPage = () => {
    const queryKey = useV3
      ? storeSpecialsQueryKey(
          storeId || 0,
          SPECIALS_PAGE_LIMIT,
          'default',
          undefined,
          true
        )
      : storeSpecialsQueryKeys.storeSpecialsPage(storeId, SPECIALS_PAGE_LIMIT);

    queryClient.setQueryData<InfiniteData<unknown>>(queryKey, (data) => {
      if (!data) return data;
      return {
        pages: data.pages.slice(0, 1),
        pageParams: data.pageParams.slice(0, 1),
      };
    });
  };

  const contextValue = useMemo(
    () => ({
      fetchNextSpecialsPage,
      hasNextSpecialsPage: !!hasNextSpecialsPage,
      isLoadingStore: loading,
      loadingSpecials,
      loadingSpecialsCards,
      refetchSpecialsPage: handleRefetchSpecialsPage,
      specialIds,
      specials: allSpecials,
      specialsCards,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleRefetchSpecialsPage closes over useV3; include it explicitly
    [
      allSpecials,
      fetchNextSpecialsPage,
      hasNextSpecialsPage,
      loading,
      loadingSpecials,
      loadingSpecialsCards,
      specialIds,
      specialsCards,
      useV3,
    ]
  );

  return (
    <SpecialsContext.Provider value={contextValue}>
      {children}
    </SpecialsContext.Provider>
  );
};

export const useSpecials = () => {
  const context = useContext(SpecialsContext);

  if (!Object.keys(context).length) {
    throw new Error('useSpecials must be used within a SpecialsProvider');
  }

  return context;
};
